/**
 * Bid decision readiness for cockpit / submit gate (domain fields only).
 */
export type BidReadinessState = 'READY' | 'BLOCKED' | 'IN_PROGRESS';

export type BidReadinessCheck = {
  id: string;
  group: string;
  label: string;
  state: BidReadinessState;
  detail?: string;
};

export type BidReadinessResult = {
  overall: BidReadinessState;
  canSubmit: boolean;
  submitBlockedReason?: string;
  checks: BidReadinessCheck[];
  groups: Array<{ name: string; state: BidReadinessState; checks: BidReadinessCheck[] }>;
};

const OUTCOMES = new Set(['GO', 'NO_GO', 'CONDITIONAL', 'PENDING']);

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function rollup(states: BidReadinessState[]): BidReadinessState {
  if (states.some((s) => s === 'BLOCKED')) return 'BLOCKED';
  if (states.length && states.every((s) => s === 'READY')) return 'READY';
  return 'IN_PROGRESS';
}

export function buildBidDecisionReadiness(row: Record<string, unknown>): BidReadinessResult {
  const tenderId = asStr(row.tender_id || row.tenderId);
  const outcome = asStr(row.outcome || row.decision || 'PENDING').toUpperCase();
  const status = asStr(row.status || 'Draft');
  const statusNorm = status.toLowerCase();
  const notes = asStr(row.notes || row.rationale);

  const checks: BidReadinessCheck[] = [];

  checks.push({
    id: 'link-tender',
    group: 'Linkage',
    label: 'Tender linked',
    state: tenderId ? 'READY' : 'BLOCKED',
    detail: tenderId ? undefined : 'Tender required',
  });

  if (!OUTCOMES.has(outcome) || outcome === 'PENDING') {
    checks.push({
      id: 'outcome',
      group: 'Decision',
      label: `Outcome: ${outcome || 'PENDING'}`,
      state: 'IN_PROGRESS',
      detail: 'Select GO, NO_GO, or CONDITIONAL before submit',
    });
  } else {
    checks.push({
      id: 'outcome',
      group: 'Decision',
      label: `Outcome: ${outcome}`,
      state: 'READY',
    });
  }

  checks.push({
    id: 'notes',
    group: 'Decision',
    label: 'Rationale / notes',
    state: notes ? 'READY' : 'IN_PROGRESS',
    detail: notes ? undefined : 'Recommended before submit',
  });

  if (statusNorm === 'approved') {
    checks.push({
      id: 'approval',
      group: 'Approval',
      label: 'Approved',
      state: 'READY',
    });
  } else if (statusNorm === 'submitted') {
    checks.push({
      id: 'approval',
      group: 'Approval',
      label: 'Submitted — pending approval',
      state: 'IN_PROGRESS',
    });
  } else if (statusNorm === 'rejected') {
    checks.push({
      id: 'approval',
      group: 'Approval',
      label: 'Rejected — can re-submit',
      state: 'IN_PROGRESS',
    });
  } else {
    checks.push({
      id: 'approval',
      group: 'Approval',
      label: `Status: ${status}`,
      state: 'IN_PROGRESS',
      detail: 'Submit to start Approval Runtime',
    });
  }

  const groupNames = Array.from(new Set(checks.map((c) => c.group)));
  const groups = groupNames.map((name) => {
    const gChecks = checks.filter((c) => c.group === name);
    return { name, state: rollup(gChecks.map((c) => c.state)), checks: gChecks };
  });

  let canSubmit = false;
  let submitBlockedReason: string | undefined;
  if (!['Draft', 'Rejected'].includes(status)) {
    submitBlockedReason = `Status ${status} cannot be submitted`;
  } else if (!tenderId) {
    submitBlockedReason = 'Tender required';
  } else if (outcome === 'PENDING' || !OUTCOMES.has(outcome)) {
    submitBlockedReason = 'Set outcome to GO, NO_GO, or CONDITIONAL';
  } else {
    canSubmit = true;
  }

  return {
    overall: rollup(checks.map((c) => c.state)),
    canSubmit,
    submitBlockedReason,
    checks,
    groups,
  };
}

export function bidReadinessTagColor(state: BidReadinessState): string {
  switch (state) {
    case 'READY':
      return 'success';
    case 'BLOCKED':
      return 'error';
    default:
      return 'processing';
  }
}
